import queryString from 'query-string'
import { PageLifecycleProviderRenderProps } from 'page-lifecycle-provider'
import { DataProviderEvents } from 'react-ssr-data-loader'
import { Logger } from 'typescript-log'

import { toDataLayerEvent, handlePageEventsInDatalayer } from 'component-library/tracking/gtm/gtm'
import { nielsenSoftPageLoad } from 'component-library/tracking/nielsen/nielsen'
import { purgeTruskin } from 'component-library/advertising/ad-helpers/truskin'

import { HttpError } from 'common/helpers/http'
import { TogglesState } from 'common/feature-togglings/reducer'
import { isFeatureEnabled } from 'common/feature-togglings/features'
import { raiseLotameEvent } from 'common/advertising/lpid'
import { DataLayerEventName } from 'common/helpers/DataLayerEventName'
import { GetDcrMeta } from 'component-library/tracking/nielsen/nielsen-meta'
import { AllEvents } from 'common/events/all-events'

let externalEventsDisabled = false

export const disableExternalEvents = () => (externalEventsDisabled = true)

export function handleDataProviderEvent(
    pageProps: PageLifecycleProviderRenderProps,
    // We want to pass the logger in here as it could
    logger: Logger
): ((event: DataProviderEvents) => void) | undefined {
    return event => {
        if (event.type === 'state-changed') {
            return
        }

        const { data } = event
        const dataResourceInfo = `${data.resourceType} - ${data.resourceLoadParamsHash}`

        if (event.type === 'begin-loading-event') {
            logger.debug(`client - Signal start loading data: ${dataResourceInfo}`)
            pageProps.beginLoadingData()
        } else if (event.type === 'end-loading-event') {
            logger.debug(`client - Signal end loading data: ${dataResourceInfo}`)
            pageProps.endLoadingData()
        } else if (event.type === 'load-error') {
            if (event.data.error.message === 'failed-data-load') {
                return
            }

            logger.debug(`client - Signal data load failed: ${dataResourceInfo}`)
            if (event.data.error instanceof HttpError || event.data.error.message === '404') {
                return
            }

            logger.error({ err: event.data.error }, 'client - Data load failed with unknown error')
        }
    }
}

/** Ensures Bonzai units are cleared on loading a new page as Bonzai ad units don't do this themselves. */
const handleBonzaiAds = (event: AllEvents): void => {
    if (event.type !== 'page-load-started') {
        return
    }

    const bonzaiAds = document.querySelectorAll('iframe[adcontainer]')

    for (let index = 0; index < bonzaiAds.length; ++index) {
        const bonzaiNode = bonzaiAds[index]
        if (!bonzaiNode) {
            continue
        }
        const bonzaiParent = bonzaiNode.parentNode
        if (!bonzaiParent) {
            continue
        }

        bonzaiParent.removeChild(bonzaiNode)
    }
}

const handleTruskinAds = (event: AllEvents): void => {
    if (event.type === 'page-load-started') {
        purgeTruskin()
    }
}

const dataLayerEventHandler = handlePageEventsInDatalayer<AllEvents>(
    (event: AllEvents, actualPageVariables: any) => {
        if (event.type === 'route-info-resolver.failed' || event.type === 'page-render.failed') {
            return
        }
        return toDataLayerEvent(event, actualPageVariables)
    }
)

export function handlePageEvents(
    event: AllEvents,
    log: Logger,
    toggleState: TogglesState,
    actualPageVariables: any,
    lotameId: number,
    nielsenSiteName: string,
    getDcrMeta: GetDcrMeta
) {
    const nielsenEnabled = isFeatureEnabled(toggleState, 'nielsen-events')

    log.debug(event, 'Page event raised')

    dataLayerEventHandler(event, log, actualPageVariables)
    handleBonzaiAds(event)
    handleTruskinAds(event)

    if (event.type === DataLayerEventName.breachScreenAvailable) {
        raiseLotameEvent(lotameId)
    }
    if (
        event.type === DataLayerEventName.breachScreenComplete ||
        event.type === DataLayerEventName.subscribeComplete
    ) {
        // DPT-942: Prevent multiple breachscreen.complete events triggering
        try {
            if (typeof window !== 'undefined') {
                const {
                    productName,
                    productSku,
                    transactionTax,
                    transactionTotal,
                    clickOrigin,
                    ...search
                } = queryString.parse(location.search)

                // We want to retain search params except all the breach screen params except transactionId, which
                // is needed by gtm, by not having the clickOrigin present the breachscreen.complete and subscribe.complete won't fire.
                const searchExcludingBreachParams = queryString.stringify(search)

                // Only add the search params on if there are some
                const url = `${window.location.pathname}${window.location.hash}${
                    searchExcludingBreachParams.length > 0 ? `?${searchExcludingBreachParams}` : ''
                }`

                window.history.replaceState({}, document.title, url)
            }
        } catch (err) {
            // not much we can do
        }
    }

    if (event.type === DataLayerEventName.authLogin) {
        /**
            DPO-543: auth.login fires twice, the second time it fires it reports the
            loginProvider and socialProviders from gigya.
            To prevent the lotame event from being raised twice, we check each auth.login
            event to only raise the lotame event once.
        */
        if (!event.payload.loginProvider && !event.payload.socialProviders) {
            raiseLotameEvent(lotameId, 'User-Login', event.payload.gigyaId)
        }
    }

    if (event.type === 'page-load-complete' && !externalEventsDisabled) {
        if (nielsenEnabled) {
            nielsenSoftPageLoad(log, getDcrMeta, nielsenSiteName)
        }
    }
}
